/**
 * useScrollNavigation - Custom hook for smooth scroll navigation
 *
 * Handles scroll-to navigation for hash links that work across different pages.
 * Consolidates the repetitive navigation logic from Layout component.
 */

import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const useScrollNavigation = () => {
  const location = useLocation();

  /**
   * Navigate to a section, handling both same-page and cross-page navigation
   * @param {string} sectionId - The ID of the section to scroll to (without #)
   * @param {Function} onNavigate - Optional callback after navigation
   */
  const navigateToSection = useCallback((sectionId, onNavigate) => {
    if (location.pathname !== "/") {
      // Cross-page navigation: update URL and trigger navigation
      const newUrl = `/#${sectionId}`;
      window.history.pushState({}, "", newUrl);
      window.dispatchEvent(new PopStateEvent("popstate"));
    } else {
      // Same-page navigation: scroll directly
      const element = document.getElementById(sectionId);
      element?.scrollIntoView({ behavior: "smooth" });
    }

    // Execute callback if provided (e.g., close mobile menu)
    if (onNavigate) {
      onNavigate();
    }
  }, [location.pathname]);

  /**
   * Create a click handler for navigation links
   * @param {string} sectionId - The ID of the section to scroll to
   * @param {Function} onNavigate - Optional callback after navigation
   * @returns {Function} Click event handler
   */
  const createNavigationHandler = useCallback((sectionId, onNavigate) => {
    return (e) => {
      e.preventDefault();
      navigateToSection(sectionId, onNavigate);
    };
  }, [navigateToSection]);

  return {
    navigateToSection,
    createNavigationHandler,
  };
};

export default useScrollNavigation;